import { Link } from "react-router-dom";
import { ArrowRight, Eye, Heart, Trophy } from "lucide-react";

const ChannelAvatar = ({ ch }) => {
  if (ch.logo) {
    return <img src={ch.logo} alt={ch.name} className="w-24 h-24 sm:w-28 sm:h-28 object-contain rounded-2xl bg-black/40 p-2 shrink-0 ring-2 ring-[color:var(--accent)]/30" />;
  }
  return (
    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl shrink-0 flex items-center justify-center font-display text-5xl text-white" style={{ background: ch.color }}>
      {ch.name?.[0]}
    </div>
  );
};

const WinnerCard = ({ winner }) => (
  <Link
    to={winner.shorts_only ? `/shorts/${winner.id}` : `/channel/${winner.id}`}
    data-testid="creator-winner"
    className="card-hover relative rounded-2xl overflow-hidden border border-[color:var(--border)] block group"
    style={{ background: `linear-gradient(135deg, ${winner.color}40, transparent 60%), var(--bg-elev)` }}
  >
    <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-black text-[10px] uppercase tracking-widest font-bold shadow-lg">
      <Trophy className="w-3 h-3" /> #1 du mois
    </div>
    <div className="p-7 flex items-center gap-5">
      <ChannelAvatar ch={winner} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent)] mb-1">{winner.category}</div>
        <h3 className="font-display text-3xl sm:text-4xl leading-tight mb-2 group-hover:text-[color:var(--accent)] transition">{winner.name}</h3>
        <div className="flex flex-wrap items-center gap-4 text-xs text-[color:var(--text-muted)]">
          <span className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {winner.views_30d} vues / 30j</span>
          <span className="inline-flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" /> {winner.likes_total} likes</span>
        </div>
      </div>
    </div>
  </Link>
);

const Leaderboard = ({ leaderboard }) => (
  <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg-elev)] p-5" data-testid="creator-leaderboard">
    <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] mb-3">Classement</div>
    <ol className="space-y-1.5">
      {leaderboard.slice(1, 6).map((c, i) => (
        <li key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[color:var(--bg-elev-2)] transition">
          <span className="w-6 h-6 rounded-full text-[10px] font-display flex items-center justify-center bg-[color:var(--bg)] text-[color:var(--text-muted)] border border-[color:var(--border)]">{i + 2}</span>
          {c.logo
            ? <img src={c.logo} alt={c.name} className="w-8 h-8 object-contain rounded bg-black/30 p-0.5 shrink-0" />
            : <span className="w-8 h-8 rounded shrink-0" style={{ background: c.color }} />}
          <Link to={`/channel/${c.id}`} className="flex-1 min-w-0 text-sm truncate hover:text-[color:var(--accent)] transition">{c.name}</Link>
          <span className="text-[10px] text-[color:var(--text-muted)] tabular-nums shrink-0 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {c.views_30d}</span>
        </li>
      ))}
      {leaderboard.length <= 1 && <li className="text-xs text-[color:var(--text-muted)] italic text-center py-3">Le classement se construit avec les vues du mois.</li>}
    </ol>
  </div>
);

export const CreatorOfMonthSection = ({ creator }) => {
  if (!creator?.winner) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-6 py-12 border-t border-[color:var(--border)]">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Trophy className="w-5 h-5 text-[color:var(--accent)]" />
          <h2 className="font-display text-3xl">Créateur du mois</h2>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)]" data-testid="creator-month">{creator.month}</span>
        </div>
        <Link to="/channels" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--accent)] transition-colors">
          Toutes les chaînes <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6" data-testid="creator-of-month">
        <WinnerCard winner={creator.winner} />
        <Leaderboard leaderboard={creator.leaderboard} />
      </div>
    </section>
  );
};
